import { useState } from "react";
import { CaretRightIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { McpServerConfig, McpServerType } from "../config/mcpServers";
import type { RuntimeToolDef } from "../tools/runtimeTypes";

const SERVER_TYPES: McpServerType[] = ["http", "sse", "stdio"];

interface McpToolsPanelProps {
  servers: McpServerConfig[];
  tools: Record<string, RuntimeToolDef>;
  onServerChange: (index: number, updated: McpServerConfig) => void;
  onAddServer: () => void;
  onRemoveServer: (index: number) => void;
  onReconnect: () => void;
  isConnecting: boolean;
}

export function McpToolsPanel({
  servers,
  tools,
  onServerChange,
  onAddServer,
  onRemoveServer,
  onReconnect,
  isConnecting,
}: McpToolsPanelProps) {
  const [expanded, setExpanded] = useState(true);

  const toolEntries = Object.entries(tools);

  // Group tools by server prefix
  const grouped = new Map<string, { name: string; description: string }[]>();
  for (const server of servers) {
    const prefix = server.name
      .replace(/[^a-zA-Z0-9]/g, "_")
      .replace(/_+/g, "_");
    grouped.set(server.name, []);
    for (const [key, tool] of toolEntries) {
      if (key.startsWith(prefix + "__")) {
        grouped.get(server.name)!.push({
          name: key,
          description: tool.definition.description,
        });
      }
    }
  }

  return (
    <section className="border-b border-border bg-muted/40 font-sans text-sm text-foreground">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setExpanded((p) => !p)}
        aria-expanded={expanded}
        className="w-full justify-start rounded-none px-3"
      >
        <CaretRightIcon
          className={`transition-transform ${expanded ? "rotate-90" : ""}`}
          aria-hidden="true"
        />
        MCP Servers — {toolEntries.length} tool
        {toolEntries.length !== 1 ? "s" : ""} discovered
      </Button>

      {expanded && (
        <div className="px-3 pb-2">
          {servers.map((server, idx) => {
            const serverTools = grouped.get(server.name) ?? [];
            return (
              <div key={idx} className="mb-2">
                <div className="flex items-center gap-1.5">
                  <span
                    className={`size-2 shrink-0 rounded-full ${
                      serverTools.length > 0 ? "bg-success" : "bg-destructive"
                    }`}
                    aria-label={
                      serverTools.length > 0 ? "Connected" : "Disconnected"
                    }
                  />
                  <Input
                    size="sm"
                    value={server.name}
                    onChange={(e) =>
                      onServerChange(idx, { ...server, name: e.target.value })
                    }
                    placeholder="server-name"
                    aria-label="Server name"
                    className="w-28 font-mono"
                  />
                  <Select
                    value={server.type}
                    onValueChange={(value) =>
                      onServerChange(idx, {
                        ...server,
                        type: value as McpServerType,
                      })
                    }
                  >
                    <SelectTrigger
                      size="xs"
                      aria-label="Server type"
                      className="w-24"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVER_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    size="sm"
                    value={server.url}
                    onChange={(e) =>
                      onServerChange(idx, { ...server, url: e.target.value })
                    }
                    placeholder="http://localhost:3001/"
                    aria-label="Server URL"
                    className="flex-1 font-mono"
                  />
                  <Button
                    variant="outline"
                    size="xs"
                    onClick={onReconnect}
                    disabled={isConnecting}
                    className="shrink-0"
                  >
                    {isConnecting ? "Connecting..." : "Reconnect"}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => onRemoveServer(idx)}
                    title="Remove server"
                    aria-label={`Remove ${server.name || "server"}`}
                    className="shrink-0 text-destructive"
                  >
                    <TrashIcon aria-hidden="true" />
                  </Button>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {serverTools.length} tool
                    {serverTools.length !== 1 ? "s" : ""}
                  </span>
                </div>
                {serverTools.length > 0 && (
                  <ul className="ml-5 mt-1 list-none space-y-1 p-0">
                    {serverTools.map((tool) => (
                      <li key={tool.name}>
                        <Badge size="sm" variant="neutral" type="ghost">
                          <code>{tool.name}</code>
                        </Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
          <Button variant="outline" size="xs" onClick={onAddServer}>
            <PlusIcon aria-hidden="true" />
            Add Server
          </Button>
        </div>
      )}
    </section>
  );
}
